import { type D2CSiteImageItem } from '@orcar/common';
import { getPrimaryColor, JEJURO, siteImageUrl } from '@orcar/common-d2c';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

type HeroSlide = {
  id: string;
  img: string;
  /** 모바일 전용 이미지(1:1 등). 없으면 img를 그대로 사용. */
  imgMobile?: string;
  eyebrow?: string;
  title?: [string, string];
  subtitle?: string;
  /** 프로모 오버레이(대형 % 강조 + 강조바). title 대신 사용. */
  promo?: { top?: string; lead: string; percent: string; tail: string };
  /** CTA 필(있으면 노출). 클릭 시 슬라이드 동작(검색)으로 이어진다. */
  cta?: string;
  /** 밝은 낮 사진용 — 스크림을 완화하고 사진을 살짝 밝게, 카피 폭을 좁혀 좌측에 고정한다. */
  variant?: 'bright';
};

// 회사가 ERP 자사몰관리 > 사이트 내용에서 고친다. 글이 사진에 들어 있는 장은
// 문구 없이 이미지만 보인다. 데스크탑(와이드)·모바일(1:1) 이미지를 각각 쓴다.
const toSlide = (item: D2CSiteImageItem): HeroSlide => ({
  id: item.id,
  img: siteImageUrl(item.image, 1920),
  imgMobile: item.mobileImage
    ? siteImageUrl(item.mobileImage, 1080)
    : undefined,
  eyebrow: item.eyebrow || undefined,
  title:
    item.title || item.titleLine2
      ? [item.title ?? '', item.titleLine2 ?? '']
      : undefined,
  subtitle: item.subtitle || undefined,
  variant: item.brightPhoto ? 'bright' : undefined,
});

const AUTOPLAY_MS = 6000;
const TRANSITION_MS = 700;

const FIRST_REAL = 1;

type BannerRef = { id: string; index: number };

type HeroSliderProps = {
  isMobile: boolean;
  /** 지금 보여야 하는 첫 화면 이미지. 내용을 받기 전에는 비어 있다. */
  items: D2CSiteImageItem[];
  /** 검색 위젯(PC: DateTimeOption, 모바일: MobileSearchBox)을 슬롯으로 받는다. */
  searchSlot: ReactNode;
  /** 슬라이드가 처음 노출될 때(1회) */
  onBannerView?: (banner: BannerRef) => void;
  /** 슬라이드를 클릭했을 때 */
  onBannerClick?: (banner: BannerRef) => void;
};

const HeroSlider = ({
  isMobile,
  items,
  searchSlot,
  onBannerView,
  onBannerClick,
}: HeroSliderProps) => {
  // 실제 첫 슬라이드(클론 다음)에서 시작
  const [pos, setPos] = useState(FIRST_REAL);
  const [animate, setAnimate] = useState(true);

  const slides = useMemo(() => items.map(toSlide), [items]);
  const real = slides.length;
  // 앞뒤로 클론을 붙여 양방향 무한 루프를 만든다. [last, ...slides, first]
  const rendered = useMemo(
    () => (real ? [slides[real - 1], ...slides, slides[0]] : []),
    [slides, real],
  );
  const total = rendered.length;
  const lastClone = total - 1;

  const activeIndex = (pos - FIRST_REAL + real) % real;

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches,
  ).current;

  const move = (delta: number) => {
    setAnimate(true);
    setPos((prev) => prev + delta);
  };
  const jump = (index: number) => {
    setAnimate(true);
    setPos(index + FIRST_REAL);
  };

  useEffect(() => {
    if (prefersReducedMotion || !real) return;
    const timer = window.setInterval(() => {
      setAnimate(true);
      setPos((prev) => prev + 1);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [prefersReducedMotion, real]);

  // 클론에 도달하면 트랜지션이 끝난 뒤 실제 슬라이드로 무점프 스냅한다.
  // (onTransitionEnd 대신 타이머로 처리해 이벤트 누락 시 빈 화면이 되는 문제를 방지)
  useEffect(() => {
    if (pos !== lastClone && pos !== 0) return;
    const snapTo = pos === lastClone ? FIRST_REAL : real;
    const timer = window.setTimeout(() => {
      setAnimate(false);
      setPos(snapTo);
    }, TRANSITION_MS);
    return () => window.clearTimeout(timer);
  }, [pos, lastClone, real]);

  // 스냅(트랜지션 off) 직후 다음 프레임에 트랜지션 복구
  useEffect(() => {
    if (animate) return;
    const raf = window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setAnimate(true)),
    );
    return () => window.cancelAnimationFrame(raf);
  }, [animate]);

  // 슬라이드 구성(모바일/데스크탑)이 바뀌면 위치를 초기화한다.
  useEffect(() => {
    setPos(FIRST_REAL);
  }, [real]);

  // 각 슬라이드가 처음 보일 때 노출 이벤트를 1회 발화한다(자동재생 중복 방지).
  const viewedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const slide = slides[activeIndex];
    if (!slide || viewedRef.current.has(slide.id)) return;
    viewedRef.current.add(slide.id);
    onBannerView?.({ id: slide.id, index: activeIndex });
  }, [activeIndex, slides, onBannerView]);

  return (
    <Root>
      <Viewport isMobile={isMobile}>
        <Track
          $total={total}
          $animate={animate}
          style={{ transform: `translateX(-${(pos * 100) / total}%)` }}
        >
          {rendered.map((slide, i) => {
            const hasCopy = !!(
              slide.eyebrow ||
              slide.title ||
              slide.subtitle ||
              slide.promo ||
              slide.cta
            );
            return (
              <Slide
                key={`${i}-${slide.img}`}
                $img={isMobile && slide.imgMobile ? slide.imgMobile : slide.img}
                $total={total}
                $bright={slide.variant === 'bright'}
                onClick={() =>
                  onBannerClick?.({
                    id: slide.id,
                    index: (i - FIRST_REAL + real) % real,
                  })
                }
              >
                {hasCopy && (
                  <Scrim isMobile={isMobile} $variant={slide.variant} />
                )}
                {hasCopy && (
                  <Copy
                    isMobile={isMobile}
                    $bright={slide.variant === 'bright'}
                  >
                    {slide.eyebrow && <Eyebrow>{slide.eyebrow}</Eyebrow>}
                    {slide.promo ? (
                      <>
                        <PromoTitle>
                          {slide.promo.top && (
                            <PromoTop>{slide.promo.top}</PromoTop>
                          )}
                          <PromoBig isMobile={isMobile}>
                            {slide.promo.lead} <Pct>{slide.promo.percent}</Pct>{' '}
                            {slide.promo.tail}
                          </PromoBig>
                        </PromoTitle>
                        <Accent />
                      </>
                    ) : (
                      slide.title && (
                        <Title>
                          {slide.title[0]}
                          <br />
                          {slide.title[1]}
                        </Title>
                      )
                    )}
                    {slide.subtitle && <Subtitle>{slide.subtitle}</Subtitle>}
                    {slide.cta && <Cta isMobile={isMobile}>{slide.cta}</Cta>}
                  </Copy>
                )}
              </Slide>
            );
          })}
        </Track>

        {!isMobile && real > 0 && (
          <>
            <Arrow $side='left' onClick={() => move(-1)} aria-label='이전 배너'>
              ‹
            </Arrow>
            <Arrow $side='right' onClick={() => move(1)} aria-label='다음 배너'>
              ›
            </Arrow>
          </>
        )}

        {!isMobile && real > 0 && (
          <Controls>
            <Dots>
              {slides.map((slide, i) => (
                <Dot
                  key={`dot-${i}`}
                  $active={i === activeIndex}
                  onClick={() => jump(i)}
                  aria-label={`${i + 1}번 배너`}
                />
              ))}
            </Dots>
            <Counter>
              {String(activeIndex + 1).padStart(2, '0')}
              <span> / {String(real).padStart(2, '0')}</span>
            </Counter>
          </Controls>
        )}
      </Viewport>

      <Dock isMobile={isMobile}>{searchSlot}</Dock>
    </Root>
  );
};

export default HeroSlider;

const Root = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Viewport = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: 100%;
  /* 데스크탑: 화면을 넓게 채우되(풀와이드) 낮고 넓은 와이드 히어로(24:7 ≈ 3.43:1).
     1920px에서 높이 560px가 되며, 좁은 화면에선 그에 비례해 낮아진다.
     초대형 모니터에서 지나치게 넓어지지 않도록 폭 상한(1920px)을 둔다.
     배너 이미지는 이 비율(2400x700 등)로 내보내고 글씨를 가운데 안전영역에 두면
     어느 폭에서도 잘리지 않는다. */
  ${({ isMobile }) =>
    isMobile
      ? css`
          height: 380px;
        `
      : css`
          margin: 0 auto;
          max-width: 1920px;
          aspect-ratio: 24 / 7;
        `}
  overflow: hidden;
  background-color: #06141f;
`;

const Track = styled.div<{ $total: number; $animate: boolean }>`
  display: flex;
  height: 100%;
  width: ${({ $total }) => `${$total * 100}%`};
  transition: ${({ $animate }) =>
    $animate
      ? `transform ${TRANSITION_MS}ms cubic-bezier(0.65, 0, 0.2, 1)`
      : 'none'};
`;

const Slide = styled.div<{ $img?: string; $total: number; $bright?: boolean }>`
  position: relative;
  flex: 0 0 ${({ $total }) => 100 / $total}%;
  height: 100%;
  cursor: pointer;

  ${({ $img }) =>
    $img &&
    css`
      background-image: url('${$img}');
      background-size: cover;
      background-position: center;
    `}

  ${({ $bright }) =>
    $bright &&
    css`
      filter: brightness(1.08) saturate(1.05);
    `}
`;

const Scrim = styled.div<{ isMobile: boolean; $variant?: 'bright' }>`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      90deg,
      rgba(4, 14, 23, 0.82) 0%,
      rgba(4, 14, 23, 0.5) 40%,
      rgba(4, 14, 23, 0.12) 70%,
      rgba(4, 14, 23, 0) 100%
    ),
    linear-gradient(0deg, rgba(4, 14, 23, 0.45) 0%, rgba(4, 14, 23, 0) 35%);

  ${({ isMobile }) =>
    isMobile &&
    css`
      background: linear-gradient(
        180deg,
        rgba(4, 14, 23, 0.55) 0%,
        rgba(4, 14, 23, 0.3) 45%,
        rgba(4, 14, 23, 0.75) 100%
      );
    `}

  /* 밝은 낮 사진: 좌측만 살짝 눌러 가독성 확보하고 우측(건물)은 빠르게 투명 처리해 밝게 노출 */
  ${({ $variant, isMobile }) =>
    $variant === 'bright' &&
    (isMobile
      ? css`
          background: linear-gradient(
            180deg,
            rgba(4, 14, 23, 0.4) 0%,
            rgba(4, 14, 23, 0.12) 42%,
            rgba(4, 14, 23, 0.5) 100%
          );
        `
      : css`
          background: linear-gradient(
            90deg,
            rgba(4, 14, 23, 0.6) 0%,
            rgba(4, 14, 23, 0.28) 30%,
            rgba(4, 14, 23, 0.06) 55%,
            rgba(4, 14, 23, 0) 72%
          );
        `)}
`;

const Copy = styled.div<{ isMobile: boolean; $bright?: boolean }>`
  position: relative;
  z-index: 2;
  /* 넓은 화면에서 카피를 화면 왼쪽에 더 붙인다(디자인 피드백). 중앙 1200 컬럼
     대신 넓은 폭 + 좌측 여백으로 좌측 정렬 느낌을 준다. */
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 clamp(24px, 4vw, 64px);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  color: ${({ theme }) => theme.COLOR.WHITE};

  ${({ isMobile }) =>
    isMobile &&
    css`
      padding: 0 24px;
      justify-content: flex-start;
      padding-top: 56px;
    `}

  /* 밝은 낮 사진: 카피를 좌측에 붙여 폭을 좁혀 우측 건물이 가려지지 않게 한다 */
  ${({ $bright }) =>
    $bright &&
    css`
      h1,
      p {
        max-width: 460px;
      }
    `}
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px 7px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  backdrop-filter: blur(6px);
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;
  letter-spacing: 0.01em;
  margin-bottom: 20px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${getPrimaryColor(JEJURO)};
    box-shadow: 0 0 0 4px rgba(0, 145, 232, 0.28);
  }
`;

const Title = styled.h1`
  margin: 0 0 16px;
  ${({ theme }) => theme.FONT.H1};
  font-size: clamp(30px, 5vw, 54px);
  line-height: 1.14;
  letter-spacing: -0.04em;
  font-weight: 800;
  text-shadow: 0 4px 28px rgba(0, 0, 0, 0.4);
`;

const Subtitle = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B1};
  font-size: clamp(14px, 2.1vw, 19px);
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
`;

/* ── 프로모(특가) 오버레이: 대형 % 강조 + 강조바 + CTA ── */
// 특가 배너 전용 워엄 액센트(브랜드 블루와 별개, 오렌지 비주얼에 맞춘 세일 컬러).
const PROMO_ACCENT = '#ff5d5f';

const PromoTitle = styled.div`
  margin: 0;
`;

const PromoTop = styled.span`
  display: block;
  font-size: clamp(16px, 2.4vw, 26px);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 6px;
`;

const PromoBig = styled.div<{ isMobile?: boolean }>`
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.05em;
  color: #fff;
  text-shadow: 0 6px 30px rgba(0, 0, 0, 0.45);
  font-size: ${({ isMobile }) =>
    isMobile ? 'clamp(54px, 16vw, 96px)' : 'clamp(60px, 8.6vw, 128px)'};
`;

const Pct = styled.span`
  background: linear-gradient(180deg, #ffffff 0%, #ffe1cc 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Accent = styled.span`
  display: block;
  height: 5px;
  width: clamp(120px, 16vw, 220px);
  border-radius: 999px;
  margin: 16px 0 4px;
  background: linear-gradient(90deg, ${PROMO_ACCENT}, #ff6a2c);
`;

const Cta = styled.span<{ isMobile?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  border-radius: 999px;
  background: ${PROMO_ACCENT};
  color: #fff;
  font-weight: 800;
  white-space: nowrap;
  box-shadow: 0 12px 28px rgba(255, 60, 62, 0.36);
  font-size: ${({ isMobile }) =>
    isMobile ? '15px' : 'clamp(15px, 1.9vw, 19px)'};
  padding: ${({ isMobile }) =>
    isMobile
      ? '11px 20px'
      : 'clamp(11px, 1.2vw, 16px) clamp(20px, 2.3vw, 30px)'};
`;

const Arrow = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
  width: 46px;
  height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: ${({ theme }) => theme.COLOR.WHITE};
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition:
    background 0.2s ease,
    transform 0.2s ease;
  ${({ $side }) => ($side === 'left' ? 'left: 24px;' : 'right: 24px;')}

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

const Controls = styled.div`
  position: absolute;
  z-index: 5;
  bottom: 96px;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: ${({ theme }) => theme.WIDTH.PC_LAYOUT};
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.25s ease;

  ${({ $active }) =>
    $active &&
    css`
      background: #fff;
      width: 30px;
    `}
`;

const Counter = styled.span`
  ${({ theme }) => theme.FONT.B2};
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #fff;

  span {
    color: rgba(255, 255, 255, 0.55);
    font-weight: 600;
  }
`;

/** 검색 위젯이 히어로 하단에 걸치도록 하는 도크 */
const Dock = styled.div<{ isMobile: boolean }>`
  position: relative;
  z-index: 7;
  width: 100%;
  max-width: ${({ theme }) => theme.WIDTH.PC_LAYOUT};
  margin: ${({ isMobile }) => (isMobile ? '-40px' : '-44px')} auto 0;
  padding: 0 20px;
  display: flex;
  justify-content: center;
`;
